/** Jornada padrão: 8h48 = 528 minutos. */
export const MINUTOS_DIA_PADRAO = 528;
/** Meta padrão do dia em pontos (Boletim). */
export const META_PONTOS_DIA_PADRAO = 1600;
/** Acréscimo padrão sobre o tempo medido (troca, paradas). */
export const ACRESCIMO_TEMPO_PADRAO = 20;

export interface ConfigTempoEmbalar {
  metaPontosDia: number;
  minutosDia: number;
  /** Percentual somado ao tempo medido. Ex.: 20 → tempo × 1,20 */
  percentualAcrescimoTempo: number;
}

export interface RitmoEmbalar {
  tempoMedidoSeg: number;
  tempoAjustadoSeg: number;
  pecasPorSegundo: number;
  pecasPorMinuto: number;
  pecasPorHora: number;
  pecasPorDia: number;
}

export function normalizarNomeProdutoEmbalar(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

export function contarVolumesPalete(cfg: {
  vol2?: number | null;
  vol3?: number | null;
}): number {
  if (cfg.vol3 != null) return 3;
  if (cfg.vol2 != null) return 2;
  return 1;
}

export function formatarHorasMinutos(minutosTotais: number): string {
  const m = Math.max(0, Math.round(minutosTotais));
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${h}h${String(min).padStart(2, '0')}`;
}

/**
 * Converte texto livre de tempo para segundos.
 * Aceita: HH:MM:SS,cc · MM:SS,cc · segundos (ex.: 61,17)
 */
export function parseTempoParaSegundos(input: string): number | null {
  const bruto = String(input || '').trim();
  if (!bruto) return null;

  const normalizado = bruto.replace(/\s/g, '').replace(/,/g, '.');
  const partes = normalizado.split(':');

  if (partes.length === 3) {
    const h = Number(partes[0]);
    const m = Number(partes[1]);
    const s = Number(partes[2]);
    if (Number.isFinite(h) && Number.isFinite(m) && Number.isFinite(s) && s >= 0 && m >= 0 && h >= 0) {
      const total = h * 3600 + m * 60 + s;
      return total > 0 ? total : null;
    }
    return null;
  }

  if (partes.length === 2) {
    const m = Number(partes[0]);
    const s = Number(partes[1]);
    if (Number.isFinite(m) && Number.isFinite(s) && m >= 0 && s >= 0) {
      const total = m * 60 + s;
      return total > 0 ? total : null;
    }
    return null;
  }

  const seg = Number(normalizado);
  if (Number.isFinite(seg) && seg > 0) return seg;
  return null;
}

export function calcularRitmoEmbalar(
  tempoSeg: number,
  quantidade: number,
  config: ConfigTempoEmbalar
): RitmoEmbalar | null {
  if (!Number.isFinite(tempoSeg) || tempoSeg <= 0) return null;
  if (!Number.isFinite(quantidade) || quantidade <= 0) return null;

  const minutosDia = Math.max(1, config.minutosDia || MINUTOS_DIA_PADRAO);
  let acrescimo = Number(config.percentualAcrescimoTempo);
  if (!Number.isFinite(acrescimo) || acrescimo < 0) acrescimo = ACRESCIMO_TEMPO_PADRAO;

  const tempoAjustadoSeg = tempoSeg * (1 + acrescimo / 100);
  const tempoAjustadoMin = tempoAjustadoSeg / 60;
  const pecasPorSegundo = quantidade / tempoAjustadoSeg;
  const pecasPorMinuto = quantidade / tempoAjustadoMin;

  return {
    tempoMedidoSeg: tempoSeg,
    tempoAjustadoSeg,
    pecasPorSegundo,
    pecasPorMinuto,
    pecasPorHora: pecasPorMinuto * 60,
    pecasPorDia: pecasPorMinuto * minutosDia,
  };
}

export function chaveEntradaEmbalar(nomeNorm: string, volume: number): string {
  return `${nomeNorm}|${volume}`;
}

export type VereditoAnalisePontuacao = 'aumentar' | 'diminuir' | 'mantem';

export interface AnalisePontuacaoEmbalar {
  /** Pontuação usada na comparação (produto inteiro ou fatia do volume). */
  pontuacaoAtual: number;
  pecasDiaTeorica: number;
  pecasDiaMedida: number;
  pontuacaoSugerida: number;
  diferenca: number;
  veredito: VereditoAnalisePontuacao;
}

/** Minutos para embalar 1 peça daquele volume (tempo ajustado ÷ quantidade). */
export function minutosPorPecaEmbalar(tempoAjustadoSeg: number, quantidade: number): number {
  const q = Number(quantidade);
  if (!Number.isFinite(q) || q <= 0) return 0;
  if (!Number.isFinite(tempoAjustadoSeg) || tempoAjustadoSeg <= 0) return 0;
  return tempoAjustadoSeg / 60 / q;
}

/**
 * Divide a pontuação total do produto entre os volumes, proporcional ao tempo de cada um.
 * P_vol = P_produto × (min_vol ÷ soma_min_todos_volumes)
 * A soma das fatias = pontuação do produto.
 */
export function dividirPontuacaoPorVolumes(
  pontuacaoProduto: number,
  volumes: { volume: number; minutosPorPeca: number }[]
): Map<number, number> | null {
  const P = Number(pontuacaoProduto);
  if (!Number.isFinite(P) || P <= 0 || volumes.length === 0) return null;
  const totalMin = volumes.reduce((s, v) => s + Math.max(0, v.minutosPorPeca), 0);
  if (totalMin <= 0) return null;
  const map = new Map<number, number>();
  for (const v of volumes) {
    if (v.minutosPorPeca > 0) {
      map.set(v.volume, P * (v.minutosPorPeca / totalMin));
    }
  }
  return map.size > 0 ? map : null;
}

/**
 * Compara ritmo medido na embalagem com a pontuação cadastrada (m²).
 * pontuação sugerida = meta ÷ peças/dia medidas
 * Se sugerida > atual → embala mais devagar que o cadastro → aumentar pontuação.
 */
export function calcularAnalisePontuacaoEmbalar(
  metaPontosDia: number,
  pontuacaoAtual: number,
  pecasPorDiaMedida: number
): AnalisePontuacaoEmbalar | null {
  const meta = Math.max(1, metaPontosDia || META_PONTOS_DIA_PADRAO);
  const pont = Number(pontuacaoAtual);
  const pecas = Number(pecasPorDiaMedida);
  if (!Number.isFinite(pont) || pont <= 0) return null;
  if (!Number.isFinite(pecas) || pecas <= 0) return null;

  const pontuacaoSugerida = meta / pecas;
  const pecasDiaTeorica = meta / pont;
  const diferenca = pontuacaoSugerida - pont;
  const ratio = pontuacaoSugerida / pont;

  let veredito: VereditoAnalisePontuacao = 'mantem';
  if (ratio > 1.05) veredito = 'aumentar';
  else if (ratio < 0.95) veredito = 'diminuir';

  return {
    pontuacaoAtual: pont,
    pecasDiaTeorica,
    pecasDiaMedida: pecas,
    pontuacaoSugerida,
    diferenca,
    veredito,
  };
}

/** Análise do produto completo (1 ou N volumes) para fechar a meta cadastrada no dia. */
export interface AnaliseConjuntoEmbalar {
  pontuacaoAtual: number;
  /** Soma das pont. sugeridas por volume (1 vol = meta ÷ peças/dia medidas). */
  pontuacaoSugerida: number;
  diferencaPontuacao: number;
  /** Produtos/dia que o cadastro promete (meta ÷ P). */
  produtosPlanejadosDia: number;
  /** Produtos/dia no ritmo medido (meta ÷ P_sugerida). */
  produtosNoRitmoDia: number;
  minutosJornada: number;
  /** Minutos além da jornada para atingir a meta do cadastro (negativo = sobra tempo). */
  minutosExtra: number;
  /** Jornada + extra para embalar a meta planejada no ritmo atual. */
  minutosTotal: number;
}

/**
 * Tempo extra para cumprir a meta do cadastro (P atual) no ritmo medido.
 * P_sugerida = Σ (meta ÷ peças/dia de cada volume com tempo).
 * minutos_extra = jornada × (P_sugerida ÷ P − 1)
 */
export function calcularAnaliseConjuntoEmbalar(
  config: Pick<ConfigTempoEmbalar, 'metaPontosDia' | 'minutosDia'>,
  pontuacaoAtual: number,
  pecasPorDiaMedidasPorVolume: number[]
): AnaliseConjuntoEmbalar | null {
  const meta = Math.max(1, config.metaPontosDia || META_PONTOS_DIA_PADRAO);
  const minutosDia = Math.max(1, config.minutosDia || MINUTOS_DIA_PADRAO);
  const P = Number(pontuacaoAtual);
  if (!Number.isFinite(P) || P <= 0) return null;
  if (pecasPorDiaMedidasPorVolume.length === 0) return null;
  if (pecasPorDiaMedidasPorVolume.some((pecas) => !Number.isFinite(pecas) || pecas <= 0)) {
    return null;
  }

  const P_sug = pecasPorDiaMedidasPorVolume.reduce((s, pecas) => s + meta / pecas, 0);
  const fator = P_sug / P;
  const minutosTotal = minutosDia * fator;
  const minutosExtra = minutosTotal - minutosDia;

  return {
    pontuacaoAtual: P,
    pontuacaoSugerida: P_sug,
    diferencaPontuacao: P_sug - P,
    produtosPlanejadosDia: meta / P,
    produtosNoRitmoDia: meta / P_sug,
    minutosJornada: minutosDia,
    minutosExtra,
    minutosTotal,
  };
}

/** Ex.: +100 → "+1h40", −30 → "−30 min" */
export function formatarDeltaMinutos(minutos: number): string {
  if (!Number.isFinite(minutos)) return '—';
  const arred = Math.round(minutos);
  if (arred === 0) return '0 min';
  const sinal = arred > 0 ? '+' : '−';
  const abs = Math.abs(arred);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h > 0) return `${sinal}${h}h${String(m).padStart(2, '0')}`;
  return `${sinal}${abs} min`;
}

export type LegendaTempoExtra = 'excede' | 'sobra' | 'no_prazo';

/** Positivo = excede jornada (ruim). Negativo = sobra tempo (bom). */
export function legendaTempoExtra(minutosExtra: number): {
  tipo: LegendaTempoExtra;
  rotulo: string;
  descricao: string;
} {
  if (!Number.isFinite(minutosExtra)) {
    return { tipo: 'no_prazo', rotulo: '—', descricao: '' };
  }
  if (minutosExtra > 0.5) {
    return {
      tipo: 'excede',
      rotulo: 'Excede',
      descricao: 'Precisa de mais tempo que a jornada — atenção',
    };
  }
  if (minutosExtra < -0.5) {
    return {
      tipo: 'sobra',
      rotulo: 'Sobra',
      descricao: 'Termina antes — sobra tempo no dia (bom)',
    };
  }
  return {
    tipo: 'no_prazo',
    rotulo: 'No prazo',
    descricao: 'Ritmo alinhado ao cadastro',
  };
}
